import { useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  Chip,
  alpha,
  useTheme,
  Collapse,
  IconButton,
} from "@mui/material";
import { ChevronDown, ChevronUp, MapPin, Phone, Globe, Factory, UsersRound, Crosshair, Tag, CalendarClock, Plus, X, Hash } from "lucide-react";
import { updateMarketingOrganization } from "../../../../../../../redux/actions/marketingAction";
import { enqueueSnackbar } from "notistack";

const SIZE_OPTIONS = ["1-10", "11-50", "51-200", "200+"];
const SOURCE_OPTIONS = [
  { value: "linkedin", label: "LinkedIn" },
  { value: "website", label: "Website" },
  { value: "referral", label: "Referral" },
  { value: "cold_outreach", label: "Cold Outreach" },
  { value: "event", label: "Event" },
  { value: "other", label: "Other" },
];

const InfoRow = ({ icon: Icon, label, children }) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, minHeight: 32 }}>
      <Box sx={{ width: 20, pt: 0.5, flexShrink: 0, display: "flex", justifyContent: "center" }}>
        <Icon size={14} color={theme.palette.text.disabled} strokeWidth={1.5} />
      </Box>
      <Typography variant="caption" sx={{ width: 80, flexShrink: 0, pt: 0.5, color: theme.palette.text.secondary, fontSize: "0.7rem", fontWeight: 500 }}>
        {label}
      </Typography>
      <Box sx={{ flex: 1 }}>{children}</Box>
    </Box>
  );
};

const FicheInfo = ({ org, orgId }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(true);
  const [tagInput, setTagInput] = useState("");

  const patchField = useCallback(async (field, value) => {
    try {
      await dispatch(updateMarketingOrganization(orgId, { [field]: value }));
    } catch {
      enqueueSnackbar("Erreur mise à jour", { variant: "error" });
    }
  }, [dispatch, orgId]);

  const handleAddTag = (e) => {
    if (e.key !== "Enter" || !tagInput.trim()) return;
    const newTags = [...(org.tags || []), tagInput.trim()];
    patchField("tags", newTags);
    setTagInput("");
  };

  const handleRemoveTag = (tag) => {
    const newTags = (org.tags || []).filter((t) => t !== tag);
    patchField("tags", newTags);
  };

  const InlineEdit = ({ value, field, placeholder }) => (
    <TextField
      defaultValue={value || ""}
      onBlur={(e) => {
        if (e.target.value !== (value || "")) patchField(field, e.target.value || null);
      }}
      placeholder={placeholder}
      variant="standard"
      size="small"
      fullWidth
      InputProps={{
        disableUnderline: true,
        sx: {
          fontSize: "0.8rem", py: 0, px: 0.5, borderRadius: "4px",
          "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.04) },
          "&.Mui-focused": { bgcolor: alpha(theme.palette.primary.main, 0.06) },
        },
      }}
    />
  );

  const InlineSelect = ({ value, field, options, placeholder }) => (
    <Select
      value={value || ""}
      onChange={(e) => patchField(field, e.target.value || null)}
      displayEmpty
      size="small"
      variant="standard"
      disableUnderline
      fullWidth
      sx={{
        fontSize: "0.8rem", px: 0.5, borderRadius: "4px",
        "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.04) },
        "& .MuiSelect-select": { py: 0.25 },
      }}
      renderValue={(v) => {
        if (!v) return <Typography sx={{ fontSize: "0.8rem", color: theme.palette.text.disabled }}>{placeholder}</Typography>;
        const opt = options.find((o) => (o.value || o) === v);
        return opt?.label || opt || v;
      }}
    >
      <MenuItem value="" sx={{ fontSize: "0.8rem", color: theme.palette.text.secondary }}>—</MenuItem>
      {options.map((o) => (
        <MenuItem key={o.value || o} value={o.value || o} sx={{ fontSize: "0.8rem" }}>
          {o.label || o}
        </MenuItem>
      ))}
    </Select>
  );

  return (
    <Box>
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          px: 2.5, py: 1, cursor: "pointer",
          "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.02) },
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: 1, color: theme.palette.text.secondary }}>
          Informations
        </Typography>
        {expanded ? <ChevronUp size={14} color={theme.palette.text.disabled} /> : <ChevronDown size={14} color={theme.palette.text.disabled} />}
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ px: 2.5, pb: 2, display: "flex", flexDirection: "column", gap: 0.75 }}>
          <InfoRow icon={Hash} label="ICE">
            <InlineEdit value={org.ice} field="ice" placeholder="ICE..." />
          </InfoRow>
          <InfoRow icon={MapPin} label="Adresse">
            <InlineEdit value={org.address} field="address" placeholder="Adresse..." />
          </InfoRow>
          <InfoRow icon={MapPin} label="Ville">
            <Box sx={{ display: "flex", gap: 1 }}>
              <InlineEdit value={org.city} field="city" placeholder="Ville..." />
              <InlineEdit value={org.country} field="country" placeholder="Pays..." />
            </Box>
          </InfoRow>
          <InfoRow icon={Phone} label="Téléphone">
            <InlineEdit value={org.phone} field="phone" placeholder="Téléphone..." />
          </InfoRow>
          <InfoRow icon={Globe} label="Site web">
            <InlineEdit value={org.website} field="website" placeholder="https://..." />
          </InfoRow>
          <InfoRow icon={Factory} label="Industrie">
            <InlineEdit value={org.industry} field="industry" placeholder="Secteur..." />
          </InfoRow>
          <InfoRow icon={UsersRound} label="Taille">
            <InlineSelect value={org.size} field="size" options={SIZE_OPTIONS} placeholder="Taille..." />
          </InfoRow>
          <InfoRow icon={Crosshair} label="Source">
            <InlineSelect value={org.source} field="source" options={SOURCE_OPTIONS} placeholder="Source..." />
          </InfoRow>
          <InfoRow icon={CalendarClock} label="Relance">
            <TextField
              type="date"
              defaultValue={org.next_follow_up ? org.next_follow_up.split("T")[0] : ""}
              onBlur={(e) => {
                const val = e.target.value ? new Date(e.target.value).toISOString() : null;
                patchField("next_follow_up", val);
              }}
              variant="standard"
              size="small"
              fullWidth
              InputProps={{
                disableUnderline: true,
                sx: { fontSize: "0.8rem", px: 0.5, borderRadius: "4px", "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.04) } },
              }}
            />
          </InfoRow>
          <InfoRow icon={Tag} label="Tags">
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
              {(org.tags || []).map((tag) => (
                <Chip
                  key={tag} label={tag} size="small"
                  onDelete={() => handleRemoveTag(tag)}
                  deleteIcon={<X size={10} />}
                  sx={{ height: 22, fontSize: "0.65rem", fontWeight: 500, borderRadius: "6px" }}
                />
              ))}
              <TextField
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleAddTag}
                placeholder="+ tag"
                variant="standard"
                size="small"
                InputProps={{
                  disableUnderline: true,
                  sx: { fontSize: "0.7rem", width: 60, px: 0.5 },
                }}
              />
            </Box>
          </InfoRow>
        </Box>
      </Collapse>
    </Box>
  );
};

export default FicheInfo;
